import { Launch, User } from "@carbon/icons-react";
import { Tag } from "@carbon/react";
import { FC } from "react";
import { Link } from "react-router-dom";

import { AlertStatusTag } from "src/components/Alerts/AlertStatusTag";
import WidgetContainer, {
  WidgetContainerProps,
} from "src/dynamic-view-renderer/Widgets/WidgetContainer.tsx";
import { useT } from "src/internationalization";
import { getPropertyValueByCode } from "src/services/property";
import { AlertSummary } from "src/types/alert";
import { hasType } from "src/types/typedValue";

export type CustomerOpenAlertsSummaryWidgetProps = {
  data?: {
    alerts: AlertSummary[];
  };
} & Omit<WidgetContainerProps, "children">;

const AlertRow: FC<{ alert: AlertSummary; isLast: boolean }> = ({ alert, isLast }) => {
  const t = useT();
  const requestProperty = getPropertyValueByCode(alert.currentCase.properties ?? [], "id");
  const requestId =
    requestProperty && hasType(requestProperty.value, "string")
      ? requestProperty.value.value
      : alert.id;

  const scenarioName = alert.scenarioExecutionResult?.scenarioVersion?.scenario?.name ?? null;
  const score = alert.scenarioExecutionResult?.globalScore ?? null;

  return (
    <Link
      to={`/alert/${alert.id}`}
      className={`no-decoration-link flex flex-col gap-2 py-4 hover:bg-layer02 -mx-5 px-5 transition-colors ${
        !isLast ? "border-b border-solid border-borderSubtle01" : ""
      }`}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span className="text-sm font-medium text-textPrimary flex items-center gap-1">
          {requestId}
          <Launch size={12} className="text-textSecondary" />
        </span>
        <AlertStatusTag status={alert.status} />
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {alert.origin === "manual" ? (
          <Tag type="cool-gray" size="sm">
            {t("component.widget.customer.open.alerts.origin.manual")}
          </Tag>
        ) : (
          <>
            {scenarioName && <span className="text-sm text-textSecondary">{scenarioName}</span>}
            {score !== null && (
              <Tag type={score >= 80 ? "red" : score >= 60 ? "magenta" : "warm-gray"} size="sm">
                {t("component.widget.customer.open.alerts.score")} {score}
              </Tag>
            )}
          </>
        )}
      </div>

      <div className="flex items-center gap-4 text-xs text-textSecondary">
        <span>{alert.adminStatus.creation.toLocaleDateString("fr-FR")}</span>
        {alert.assigneeFullName ? (
          <span className="flex items-center gap-1">
            <User size={12} />
            {alert.assigneeFullName}
          </span>
        ) : (
          <span className="italic">{t("component.widget.customer.open.alerts.unassigned")}</span>
        )}
      </div>
    </Link>
  );
};

const CustomerOpenAlertsSummaryWidget: FC<CustomerOpenAlertsSummaryWidgetProps> = ({
  data,
  ...containerProps
}) => {
  const t = useT();
  const alerts = data?.alerts ?? [];

  return (
    <WidgetContainer {...containerProps}>
      {alerts.length === 0 ? (
        <p className="text-sm text-textSecondary italic">
          {t("component.widget.customer.open.alerts.empty")}
        </p>
      ) : (
        <div>
          {alerts.map((alert, index) => (
            <AlertRow key={alert.id} alert={alert} isLast={index === alerts.length - 1} />
          ))}
        </div>
      )}
    </WidgetContainer>
  );
};

export default CustomerOpenAlertsSummaryWidget;
